import {
  Button,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@design-system';

import { type UseFormReturn } from 'react-hook-form';
import { type AdvisorBasicInfoFormValues } from '@client/hooks/useAdvisorOnboardingBasicInfoForm';
import { EdjPilotRestricted } from '@/shared/components/edj-pilot-restricted';

interface AdvisorOnboardingBasicInfoStepProps {
  form: UseFormReturn<AdvisorBasicInfoFormValues>;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export function AdvisorOnboardingBasicInfoStep({
  form,
  onSubmit,
  isSubmitting,
}: AdvisorOnboardingBasicInfoStepProps) {
  const organizationDisplayName = form.watch('organizationDisplayName');

  const isDirty = form.formState.isDirty;
  const isFormValid = form.formState.isValid;

  return (
    <div className="flex flex-col items-center justify-center px-4 sm:px-0">
      <div className="w-full max-w-md space-y-6 sm:max-w-lg sm:space-y-12">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-950 sm:text-3xl">Let's get started</h2>
          <p className="text-foreground mt-2 text-sm sm:text-base">
            Please provide your basic information to set up your account.
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={e => {
              e.preventDefault();
              if (isFormValid && !isSubmitting && isDirty) {
                onSubmit();
              }
            }}
            className="space-y-4 sm:space-y-6"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6">
              <FormField
                control={form.control}
                name="advisorFirstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      First Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="advisorLastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Last Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Smith"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <EdjPilotRestricted tooltipText='Organization name is preset for the Edward Jones pilot.'>
              <FormField
                control={form.control}
                name="organizationDisplayName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Organization Name <span className="text-destructive">*</span>
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Example Firm"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                    <FormDescription>This will appear on materials your clients see</FormDescription>
                  </FormItem>
                )}
              />
            </EdjPilotRestricted>

            <EdjPilotRestricted tooltipText='Organization legal name is preset for the Edward Jones pilot.'>
              <FormField
                control={form.control}
                name="organizationName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Organization Legal Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={organizationDisplayName || 'Example Firm, LLC'}
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      Your official registered business name (defaults to organization name above)
                    </FormDescription>
                  </FormItem>
                )}
              />
            </EdjPilotRestricted>

            <div className="flex flex-col items-center pt-4">
              <Button
                type="submit"
                disabled={!isFormValid || isSubmitting || !isDirty}
                className="w-full"
              >
                {isSubmitting ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}
